import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { X } from "lucide-react";

import { cn } from "@lib/utils";

import { Button } from "./Button";

const DIALOG_CLICK_POSITION_RESET_TIMEOUT = 400;

let dialogClickPositionResetTimeout: number | null = null;

const handleDialogClickCapture = (event: MouseEvent) => {
	const root = document.documentElement;

	if (dialogClickPositionResetTimeout !== null) {
		window.clearTimeout(dialogClickPositionResetTimeout);
	}

	if (event.detail === 0) {
		root.style.removeProperty("--dialog-click-x");
		root.style.removeProperty("--dialog-click-y");
		return;
	}

	root.style.setProperty("--dialog-click-x", `${event.clientX}px`);
	root.style.setProperty("--dialog-click-y", `${event.clientY}px`);

	dialogClickPositionResetTimeout = window.setTimeout(() => {
		root.style.removeProperty("--dialog-click-x");
		root.style.removeProperty("--dialog-click-y");
		dialogClickPositionResetTimeout = null;
	}, DIALOG_CLICK_POSITION_RESET_TIMEOUT);
};

if (typeof document !== "undefined") {
	document.addEventListener("click", handleDialogClickCapture, true);
}

/** A window overlaid on either the primary window or another dialog window, rendering the content underneath inert */
const Dialog = ({ ...props }: DialogPrimitive.Root.Props) => <DialogPrimitive.Root data-slot="dialog" {...props} />;

/** Dialog trigger element */
const DialogTrigger = ({ ...props }: DialogPrimitive.Trigger.Props) => (
	<DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
);

/** Dialog portal element */
const DialogPortal = ({ ...props }: DialogPrimitive.Portal.Props) => (
	<DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
);

/** Dialog close element */
const DialogClose = ({ ...props }: DialogPrimitive.Close.Props) => (
	<DialogPrimitive.Close
		nativeButton={false}
		data-slot="dialog-close"
		render={<X strokeWidth={1.5} stroke="white" size={24} />}
		{...props}
	/>
);

/** Dialog overlay element */
const DialogOverlay = ({ className, ...props }: DialogPrimitive.Backdrop.Props) => (
	<DialogPrimitive.Backdrop
		data-slot="dialog-overlay"
		className={cn(
			`fixed inset-0 isolate z-sticky bg-black/10 duration-200 data-closed:animate-out data-closed:fade-out-0
			data-open:animate-in data-open:fade-in-0`,
			// "supports-backdrop-filter:backdrop-blur-xs"
			"bg-[#000000]/45",
			className,
		)}
		{...props}
	/>
);

/** Dialog content element */
const DialogContent = ({
	className,
	children,
	showCloseButton = true,
	...props
}: DialogPrimitive.Popup.Props & {
	showCloseButton?: boolean;
}) => (
	<DialogPortal>
		<DialogOverlay />
		<DialogPrimitive.Popup
			data-slot="dialog-content"
			className={cn(
				`bg-background ring-foreground/10 sm:max-w-sm fixed top-1/2 left-1/2 z-sticky grid w-full
				origin-[calc(var(--dialog-click-x,50vw)-50vw+50%)_calc(var(--dialog-click-y,50vh)-50vh+50%)] rounded-xl text-sm
				outline-none data-closed:animate-dialog-content-out data-open:animate-dialog-content-in`,
				// "max-w-[calc(100%-2rem)]",
				"rounded-2xl bg-block px-6",
				className,
			)}
			{...props}
		>
			{children}
			{showCloseButton ? (
				<DialogPrimitive.Close
					data-slot="dialog-close"
					render={
						<button
							className={`focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20
								aria-invalid:border-destructive group/button hover:text-foreground aria-expanded:bg-muted
								aria-expanded:text-foreground absolute top-[22px] right-[22px] inline-flex size-7 h-6 w-6 shrink-0
								cursor-pointer items-center justify-center rounded-[min(var(--radius-md),12px)] border-none
								border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none
								select-none hover:opacity-60 focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50
								in-data-[slot=button-group]:rounded-lg aria-invalid:ring-[3px] [&_svg]:pointer-events-none
								[&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-6`}
						>
							<DialogClose />
							<span className="sr-only">Close</span>
						</button>
					}
				/>
			) : null}
		</DialogPrimitive.Popup>
	</DialogPortal>
);

/** Dialog header element */
const DialogHeader = ({ className, ...props }: React.ComponentProps<"div">) => (
	<div data-slot="dialog-header" className={cn("flex flex-col gap-2", "py-5", className)} {...props} />
);

/** Dialog footer element
 * @props className - The class name to apply to the dialog footer
 * @props showCloseButton - Whether to show the close button
 *
 */
const DialogFooter = ({
	className,
	showCloseButton = false,
	children,
	...props
}: React.ComponentProps<"div"> & {
	showCloseButton?: boolean;
}) => (
	<div
		data-slot="dialog-footer"
		className={cn("bg-muted/50 sm:flex-row sm:justify-end flex gap-2 rounded-b-xl", "py-6", className)}
		{...props}
	>
		{children}
		{showCloseButton ? <DialogPrimitive.Close render={<Button type="secondary">Close</Button>} /> : null}
	</div>
);

/** Dialog title element */
const DialogTitle = ({ className, ...props }: DialogPrimitive.Title.Props) => (
	<DialogPrimitive.Title
		data-slot="dialog-title"
		className={cn(
			"font-[Inter] text-base leading-none font-medium",
			"m-0 p-0 text-lg font-bold! text-white not-italic no-underline",
			className,
		)}
		{...props}
	/>
);

Dialog.displayName = "Dialog";
DialogClose.displayName = "DialogClose";
DialogContent.displayName = "DialogContent";
DialogFooter.displayName = "DialogFooter";
DialogHeader.displayName = "DialogHeader";
DialogOverlay.displayName = "DialogOverlay";
DialogPortal.displayName = "DialogPortal";
DialogTitle.displayName = "DialogTitle";
DialogTrigger.displayName = "DialogTrigger";

export {
	Dialog,
	DialogClose,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogOverlay,
	DialogPortal,
	DialogTitle,
	DialogTrigger,
};
